import * as React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useOptimisationRuns } from '@/hooks/optimisation/use-optimisation-runs';
import { RunStatusBadge } from '@/pages/projects/components/run-status-badge';
import { RunDetail } from './components/run-detail';

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function OptimisationRunsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectIdParam = searchParams.get('projectId');
  const projectId = projectIdParam ? parseInt(projectIdParam, 10) : undefined;

  const [selectedRunId, setSelectedRunId] = React.useState<number | undefined>();
  const [compareRunId, setCompareRunId] = React.useState<number | undefined>();

  const { data, isLoading } = useOptimisationRuns({ projectId });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-5 w-5 animate-spin text-fg-muted" />
      </div>
    );
  }

  const runs = data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        {projectId && (
          <Button variant="ghost" size="sm" onClick={() => navigate(`/projects/${projectId}`)} className="-ml-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Project
          </Button>
        )}
        <h1 className="text-2xl font-bold tracking-tight">Optimisation Runs</h1>
      </div>

      {runs.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex h-32 items-center justify-center text-fg-muted text-sm">
            No optimisation runs found.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {runs.map((run) => (
            <Card
              key={run.id}
              className={[
                'cursor-pointer transition-shadow hover:shadow-md',
                selectedRunId === run.id ? 'ring-2 ring-primary' : '',
              ].join(' ')}
              onClick={() => setSelectedRunId(run.id === selectedRunId ? undefined : run.id)}
            >
              <CardContent className="flex items-center justify-between py-3 px-4 gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <RunStatusBadge status={run.runStatus} />
                  <span className="text-sm font-mono text-fg-muted">#{run.id}</span>
                  <span className="text-sm text-fg-muted truncate">
                    {formatDate(run.createdDate)}
                  </span>
                </div>
                <div className="flex items-center gap-4 shrink-0 text-sm">
                  {run.totalBarsUsed != null && (
                    <span className="text-fg-muted">{run.totalBarsUsed} bars</span>
                  )}
                  {run.algorithmUsed && (
                    <span className="text-fg-muted font-mono text-xs">{run.algorithmUsed}</span>
                  )}
                  {selectedRunId && selectedRunId !== run.id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCompareRunId(run.id);
                      }}
                    >
                      Compare
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {selectedRunId && (
        <div className="border-t pt-6">
          <RunDetail runId={selectedRunId} compareWithId={compareRunId} />
        </div>
      )}
    </div>
  );
}
